import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { prepareImageForUpload } from '../../../../services/ImageProcessor';

export const MAX_PHOTOS = 5;

export interface PhotoPickerProps {
  onChange: (photos: File[]) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  photos: File[];
}

export interface PhotoPreview {
  key: string;
  url: string;
}

export const usePhotoPicker = ({ photos, onChange, onProcessingChange }: PhotoPickerProps) => {
  const { t } = useTranslation();
  const [processingCount, setProcessingCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const previews = useMemo<PhotoPreview[]>(() => photos.map((file) => ({
    url: URL.createObjectURL(file),
    key: file.name + file.size + file.lastModified
  })), [photos]);

  useEffect(() => {
    return () => {
      previews.forEach(({ url }) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  useEffect(() => {
    onProcessingChange?.(processingCount > 0);
  }, [processingCount, onProcessingChange]);

  const isFull = photos.length + processingCount >= MAX_PHOTOS;

  const processFiles = async (selected: File[]) => {
    if (!selected.length) return;

    setErrorMessage(null);

    const remaining = MAX_PHOTOS - photos.length;
    const files = selected.slice(0, remaining);
    const exceeded = selected.length > remaining;

    if (!files.length) {
      setErrorMessage(t('meterkast:maxPhotos', { max: MAX_PHOTOS }));

      return;
    }

    setProcessingCount((c) => c + files.length);

    const results = await Promise.all(
      files.map(async (file) => {
        try {
          return { file: await prepareImageForUpload(file), ok: true as const };
        } catch {
          return { ok: false as const };
        }
      })
    );

    setProcessingCount((c) => c - files.length);

    const accepted = results.filter((r): r is { file: File; ok: true } => r.ok).map((r) => r.file);
    const failedCount = results.length - accepted.length;

    if (accepted.length > 0) {
      onChange([...photos, ...accepted]);
    }

    if (exceeded) {
      setErrorMessage(t('meterkast:maxPhotos', { max: MAX_PHOTOS }));
    } else if (failedCount > 0) {
      setErrorMessage(t('meterkast:photoError'));
    }
  };

  const handleFiles = async (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files ? Array.from(event.target.files) : [];

    event.target.value = '';

    await processFiles(selected);
  };

  const handleRemove = (index: number) => {
    const next = photos.slice();

    next.splice(index, 1);
    onChange(next);
    setErrorMessage(null);
  };

  return { t, processingCount, errorMessage, previews, isFull, processFiles, handleFiles, handleRemove };
};
